import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Helmet } from "@/components/Helmet";
import { Button } from "@/components/ui/button";
import { openAnalyticsPreferences } from "@/lib/analyticsConsent";
import { ChevronUp } from "lucide-react";

const LAST_UPDATED = "2026-01-21";

const sections = [
  { id: "overview", title: "Overview" },
  { id: "info-collect", title: "Information we collect" },
  { id: "cookies", title: "Cookies and local storage" },
  { id: "use-info", title: "How we use information" },
  { id: "share-info", title: "When we share information" },
  { id: "retention", title: "Data retention" },
  { id: "choices", title: "Your choices" },
  { id: "contact", title: "Contact" },
  { id: "anti-phishing", title: "Anti-phishing note" },
];

const summaryChips = ["Usage metadata", "Account details", "Billing", "Abuse prevention"];

export default function Privacy() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showBackToTop, setShowBackToTop] = useState(false);
  const hash = location.hash;
  const hashId = hash ? hash.replace("#", "") : "";
  const activeId = sections.some((section) => section.id === hashId) ? hashId : "overview";

  const scrollToSection = (id: string, updateHash = true) => {
    if (typeof document === "undefined") return;
    const element = document.getElementById(id);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    if (updateHash && location.hash !== `#${id}`) {
      navigate({ pathname: location.pathname, hash: `#${id}` }, { replace: false });
    }
  };

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    if (!id) return;
    const element = document.getElementById(id);
    if (!element) return;
    window.requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [hash]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Privacy Policy | ShadowScan</title>
        <meta
          name="description"
          content="ShadowScan privacy policy covering the website, customer portal, and WordPress plugin data handling."
        />
      </Helmet>

      <Navigation />

      <main className="flex-grow pt-24" id="top">
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// PRIVACY_POLICY</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-3">Privacy Policy</h1>
              <p className="text-muted-foreground">Last updated: {LAST_UPDATED}</p>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto rounded-xl border border-border/60 bg-secondary/20 p-6 md:p-8 space-y-4 text-sm text-muted-foreground">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <h2 className="text-lg font-heading font-semibold text-foreground">Plain-English privacy summary</h2>
                <div className="flex flex-wrap gap-2">
                  {summaryChips.map((chip) => (
                    <span
                      key={chip}
                      className="px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-xs text-primary"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                This section summarises what data we collect and how it is used.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We collect limited technical and account data needed to operate the website, portal, and plugin.</li>
                <li>Analytics and error monitoring are used to operate, maintain, and improve reliability and usability.</li>
                <li>Subprocessors support specific functions of the service on our behalf; see the <Link className="text-primary hover:underline" to="/subprocessors">Subprocessors</Link> page.</li>
                <li>You can opt out of non-essential analytics and request access to or deletion of your data.</li>
                <li>Questions? Contact <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a>.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
              <aside className="space-y-4">
                <div className="rounded-xl border border-border/60 bg-secondary/20 p-5 hidden lg:block sticky top-28">
                  <h3 className="text-sm font-heading font-semibold text-foreground mb-4">Sections</h3>
                  <nav>
                    <ul className="space-y-3 text-sm">
                      {sections.map((section) => {
                        const isActive = activeId === section.id;
                        return (
                          <li key={section.id}>
                            <button
                              className={`w-full text-left transition-colors ${
                                isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
                              }`}
                              onClick={() => scrollToSection(section.id)}
                              type="button"
                            >
                              {section.title}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                </div>

                <div className="lg:hidden">
                  <label htmlFor="privacy-sections" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Sections
                  </label>
                  <select
                    id="privacy-sections"
                    className="mt-2 w-full rounded-lg border border-border/60 bg-secondary/30 px-4 py-3 text-sm text-foreground"
                    value={activeId}
                    onChange={(event) => scrollToSection(event.target.value)}
                  >
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.title}
                      </option>
                    ))}
                  </select>
                </div>
              </aside>

              <div className="space-y-10 text-sm text-muted-foreground leading-relaxed">
                <section id="overview" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Overview</h2>
                  <p>
                    This Privacy Policy explains how ShadowScan (“we”, “us”) collects, uses, discloses, and protects information when you use:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>
                      Our website at <span className="font-mono">https://shadowscan.com.au</span> (the “Website”)
                    </li>
                    <li>The ShadowScan customer portal (the “Portal”)</li>
                    <li>The ShadowScan WordPress plugin (“Plugin”), where applicable</li>
                    <li>The ShadowScan Live Scan tool (limited, external checks) (the “Live Scan”)</li>
                  </ul>
                  <p>
                    Live Scan runs only on user-initiated action, is non-intrusive, and captures limited technical metadata required to deliver the service.
                  </p>
                  <p>
                    Questions? Contact <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a>.
                  </p>
                </section>

                <section id="info-collect" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Information we collect</h2>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">Information you provide</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Contact/free scan requests: name, email, website URL, optional message.</li>
                      <li>Newsletter subscriptions: email address and subscription status.</li>
                      <li>Portal accounts: email address and profile details you choose to provide (e.g., name, company, phone).</li>
                      <li>Domains you add: domain name and CMS type.</li>
                      <li>Checkout inputs: coupon or promotion codes you enter before checkout.</li>
                      <li>Support/feature requests: subject/title, message/description, and your email.</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">Security, billing, and operational data</h3>
                    <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                      <li>Billing identifiers (customer/subscription IDs and invoice references). Payment card details are processed by our payment processor.</li>
                      <li>Connector status and heartbeat metadata (timestamps, version, and environment details).</li>
                      <li>Security alert records received via connected security tooling/webhooks (may include source/attacker IP if provided).</li>
                      <li>Audit logs for sensitive actions (may include timestamps and IP address metadata).</li>
                      <li>MFA and account security data (short-lived email codes; login-location checks using IP and approximate city/country).</li>
                      <li>Rate-limiting data (IP address and/or account ID plus timestamps).</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">Device/usage data (analytics)</h3>
                    <p className="text-muted-foreground">
                      The Website/Portal may include analytics and error-monitoring tools which may collect usage and device information such as page views, interactions, approximate location, browser/device details, and identifiers stored in cookies or local storage. We use analytics providers like PostHog and Matomo to understand product usage and improve the Portal experience.
                    </p>
                    <p className="text-muted-foreground">
                      Some analytics tools may record behavioral events or session activity to help diagnose issues and improve usability. You can opt out of non-essential analytics by adjusting your browser’s cookie settings or using available opt-out controls where provided.
                    </p>
                  </div>

                </section>

                <section id="cookies" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Cookies and local storage</h2>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                    <li>Portal authentication/session data is stored in browser local storage (via our authentication provider).</li>
                    <li>Portal UI may set functional preference cookies.</li>
                    <li>Analytics/error-monitoring tools may set cookies or local storage identifiers.</li>
                  </ul>
                  <p className="text-muted-foreground">
                    You can control cookies and local storage through your browser settings. Blocking them may impact Portal functionality.
                  </p>
                  <p className="text-muted-foreground">
                    You can also manage analytics consent using the cookie banner.
                  </p>
                  <Button variant="link" className="px-0 text-primary" onClick={() => openAnalyticsPreferences()}>
                    Manage cookie preferences
                  </Button>
                </section>

                <section id="use-info" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">How we use information</h2>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                    <li>Provide and operate the Website, Portal, and Plugin features.</li>
                    <li>Authenticate users and manage Portal accounts.</li>
                    <li>Deliver requested services (e.g., scanning and remediation workflows).</li>
                    <li>Send operational emails (verification codes, security alerts, domain-change notifications).</li>
                    <li>Send newsletters/blog notifications if you subscribe (and honor unsubscribe requests).</li>
                    <li>Process billing and manage subscriptions (via our payment processor).</li>
                    <li>Validate coupons and affiliate referrals before checkout and apply discounts to billing.</li>
                    <li>Prevent abuse and maintain security (rate-limiting, logging, auditing).</li>
                    <li>Improve performance and usability (analytics and error monitoring).</li>
                  </ul>
                </section>

                <section id="share-info" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">When we share information</h2>
                  <p className="text-muted-foreground">
                    We may share information with service providers that help operate ShadowScan (for example: hosting, database, authentication, email delivery, payments, affiliate tracking, analytics, and error monitoring).
                  </p>
                  <p className="text-muted-foreground">
                    Our subprocessors are listed on the <Link className="text-primary hover:underline" to="/subprocessors">Subprocessors</Link> page.
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                    <li>Hosting, database, and authentication providers (Supabase)</li>
                    <li>Email delivery providers (Resend)</li>
                    <li>Payment processors (Stripe)</li>
                    <li>Affiliate tracking (Rewardful)</li>
                    <li>Third-party security tooling used for protection and monitoring</li>
                    <li>Analytics and error-monitoring providers (PostHog, Matomo)</li>
                  </ul>
                  <p className="text-muted-foreground">
                    We may also share information if required by law or to protect users and ShadowScan. We do not sell personal information in the ordinary commercial sense.
                  </p>
                </section>

                <section id="retention" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Data retention</h2>
                  <p className="text-muted-foreground">
                    We keep information only as long as needed for the purposes described above, including legal, accounting, and security needs. Some records are short-lived, while account, domain, alerts, and audit logs may be retained longer.
                  </p>
                </section>

                <section id="choices" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Your choices</h2>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                    <li>Unsubscribe from newsletters via the link in emails.</li>
                    <li>Control cookies and local storage via your browser settings.</li>
                    <li>Request access/correction/deletion by emailing <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a>.</li>
                  </ul>
                </section>

                <section id="contact" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Contact</h2>
                  <p className="text-muted-foreground">
                    ShadowScan - <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a>
                  </p>
                </section>

                <section id="anti-phishing" className="scroll-mt-28 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">Anti-phishing note</h2>
                  <p className="text-muted-foreground">
                    ShadowScan will never ask you to share your password, full payment card number, or one-time verification codes via email, SMS, or phone. If you receive a suspicious message claiming to be ShadowScan, contact <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a> directly.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </section>
        <button
          className={`fixed bottom-6 right-6 z-50 inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-secondary/80 text-foreground shadow-lg backdrop-blur transition duration-200 hover:text-primary ${
            showBackToTop ? "opacity-100 scale-100" : "pointer-events-none opacity-0 scale-95"
          }`}
          type="button"
          onClick={() => scrollToSection("top", false)}
          aria-label="Back to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      </main>

      <Footer />
    </div>
  );
}
